import React from 'react';
import { createPortal } from 'react-dom';
import { Theme } from '../Theme';

export type CheckboxPullDownMenuOption = {
    id: string;
    label: string;
};

type CheckboxPullDownMenuProps = {
    label: string;
    options: Array<CheckboxPullDownMenuOption>;
    selectedIds: Array<string>;
    onChange: (selectedIds: Array<string>) => void;
    summary?: string;
    ariaLabel?: string;
    style?: React.CSSProperties;
};

type MenuPosition = {
    top: number;
    left: number;
};

const MENU_OFFSET_PX = 6;

export function CheckboxPullDownMenu(props: CheckboxPullDownMenuProps) {
    const {
        label,
        options,
        selectedIds,
        onChange,
        summary,
        ariaLabel,
        style,
    } = props;
    const [isOpen, setIsOpen] = React.useState(false);
    const [menuPosition, setMenuPosition] = React.useState<MenuPosition>({ top: 0, left: 0 });
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const buttonRef = React.useRef<HTMLButtonElement | null>(null);
    const menuRef = React.useRef<HTMLDivElement | null>(null);
    const selectedIdsSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

    const updateMenuPosition = React.useCallback(() => {
        // Anchor the menu to the button in viewport coordinates so parent overflow cannot crop it.
        const buttonRect = buttonRef.current?.getBoundingClientRect();
        if (!buttonRect) {
            return;
        }

        setMenuPosition({
            top: buttonRect.bottom + MENU_OFFSET_PX,
            left: Math.max(8, buttonRect.left),
        });
    }, []);

    const closeMenu = React.useCallback(() => {
        setIsOpen(false);
    }, []);

    React.useEffect(() => {
        // Close the menu when the user clicks elsewhere on the page.
        if (!isOpen) {
            return;
        }

        const handleDocumentMouseDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                !containerRef.current?.contains(target) &&
                !menuRef.current?.contains(target)
            ) {
                closeMenu();
            }
        };

        document.addEventListener('mousedown', handleDocumentMouseDown);
        return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
    }, [closeMenu, isOpen]);

    React.useLayoutEffect(() => {
        // Keep the popup attached while the page scrolls or resizes.
        if (!isOpen) {
            return;
        }

        updateMenuPosition();
        window.addEventListener('resize', updateMenuPosition);
        window.addEventListener('scroll', updateMenuPosition, true);

        return () => {
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition, true);
        };
    }, [isOpen, updateMenuPosition]);

    const handleToggleOption = (optionId: string) => {
        // Emit a stable option-ordered list after each checkbox toggle.
        const nextSelectedIds = new Set(selectedIds);
        if (nextSelectedIds.has(optionId)) {
            nextSelectedIds.delete(optionId);
        } else {
            nextSelectedIds.add(optionId);
        }

        onChange(options
            .map((option) => option.id)
            .filter((optionId) => nextSelectedIds.has(optionId)));
    };

    const buttonText = summary || label;
    const menu = isOpen ? createPortal(
        <div
            ref={menuRef}
            style={{
                ...styles.menu,
                top: menuPosition.top,
                left: menuPosition.left,
            }}
            role='menu'
            onClick={(event) => event.stopPropagation()}
        >
            {options.map((option) => {
                const isChecked = selectedIdsSet.has(option.id);

                return (
                    <label
                        key={option.id}
                        style={styles.menuItem}
                    >
                        <input
                            type='checkbox'
                            checked={isChecked}
                            onChange={() => handleToggleOption(option.id)}
                            style={styles.checkbox}
                        />
                        <span>{option.label}</span>
                    </label>
                );
            })}
        </div>,
        document.body
    ) : null;

    return (
        <div ref={containerRef} style={{ ...styles.container, ...style }}>
            <button
                ref={buttonRef}
                type='button'
                onClick={() => {
                    if (!isOpen) {
                        updateMenuPosition();
                    }
                    setIsOpen((currentIsOpen) => !currentIsOpen);
                }}
                style={styles.button}
                aria-label={ariaLabel || label}
                aria-haspopup='menu'
                aria-expanded={isOpen}
            >
                <span style={styles.buttonLabel}>{buttonText}</span>
                <span
                    style={{
                        ...styles.chevron,
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                    aria-hidden='true'
                >
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                        <polyline
                            points='6 9 12 15 18 9'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                    </svg>
                </span>
            </button>
            {menu}
        </div>
    );
}

const styles: Record<string, React.CSSProperties> = {
    container: {
        position: 'relative',
        display: 'inline-flex',
        flexShrink: 0,
    },
    button: {
        ...Theme.Fonts.body,
        minWidth: '150px',
        height: '42px',
        padding: '8px 10px 8px 12px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.workArea,
        color: Theme.Colours.text,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
        cursor: 'pointer',
        boxSizing: 'border-box',
    },
    buttonLabel: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    chevron: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'transform 160ms ease',
    },
    menu: {
        position: 'fixed',
        zIndex: 2200,
        minWidth: '190px',
        padding: '6px',
        border: Theme.Colours.border,
        borderRadius: '8px',
        backgroundColor: Theme.Colours.workArea,
        boxShadow: '0 10px 28px rgba(0, 0, 0, 0.12)',
        boxSizing: 'border-box',
    },
    menuItem: {
        ...Theme.Fonts.body,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 10px',
        borderRadius: '6px',
        cursor: 'pointer',
        color: Theme.Colours.text,
        userSelect: 'none',
    },
    checkbox: {
        margin: 0,
        flexShrink: 0,
    },
};
